/**
 * 修改密码表单组件
 * 用于用户修改自己的密码，需要输入原密码和新密码
 */

import { LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Progress,
  Space,
  Typography,
} from 'antd';
import React from 'react';
import {
  calculatePasswordEntropy,
  getPasswordStrength,
  PasswordStrength,
  validatePasswordStrength,
} from '@/utils/passwordUtils';

interface ChangePasswordFormValues {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface ChangePasswordFormProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: (values: ChangePasswordFormValues) => Promise<void>;
}

const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({
  visible,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [passwordEntropy, setPasswordEntropy] = React.useState<number>(0);
  const [_passwordStrength, setPasswordStrength] =
    React.useState<PasswordStrength>(PasswordStrength.VeryWeak);

  // 重置表单和状态
  React.useEffect(() => {
    if (visible) {
      // 🔧 关键修复：弹框打开时立即清空所有字段
      const allFields = form.getFieldsValue();
      Object.keys(allFields).forEach((key) => {
        form.setFields([{ name: key, value: undefined }]);
      });
      setError(null);
      setPasswordEntropy(0);
      setPasswordStrength(PasswordStrength.VeryWeak);
    }
  }, [visible, form]);

  // 密码变化时实时计算熵值和强度
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    const entropy = calculatePasswordEntropy(password);
    const strength = getPasswordStrength(password);

    setPasswordEntropy(entropy);
    setPasswordStrength(strength);
  };

  // 获取熵值颜色
  const getEntropyColor = () => {
    if (passwordEntropy < 35) return '#ff4d4f';
    if (passwordEntropy < 50) return '#faad14';
    if (passwordEntropy < 60) return '#faad14';
    if (passwordEntropy < 80) return '#52c41a';
    return '#13c2c2';
  };

  // 获取熵值状态文本
  const getEntropyStatus = () => {
    if (passwordEntropy === 0) return '请输入密码';
    if (passwordEntropy < 35) return '极弱';
    if (passwordEntropy < 50) return '弱';
    if (passwordEntropy < 60) return '中等（需≥60）';
    if (passwordEntropy < 80) return '强';
    return '非常强';
  };

  // 确认密码验证
  const validateConfirmPassword = async (_rule: any, value: string) => {
    const newPassword = form.getFieldValue('newPassword');
    if (newPassword && value && newPassword !== value) {
      throw new Error('两次输入的密码不一致');
    }
  };

  // 处理表单提交
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);
      const values = await form.validateFields();

      await onSuccess({
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });

      // 成功后重置表单 - 🔧 关键修复：强制清空所有字段
      const allFields = form.getFieldsValue();
      Object.keys(allFields).forEach((key) => {
        form.setFields([{ name: key, value: undefined }]);
      });
    } catch (err: any) {
      if (err.message) {
        setError(err.message);
      } else {
        setError('密码修改失败，请重试');
      }
      console.error('密码修改失败:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <LockOutlined />
          修改密码
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={submitting}>
          取消
        </Button>,
        <Button
          key="submit"
          type="primary"
          icon={<SafetyCertificateOutlined />}
          loading={submitting}
          onClick={handleSubmit}
          disabled={passwordEntropy < 60}
        >
          确认修改
        </Button>,
      ]}
      width={520}
      destroyOnHidden
    >
      {error && (
        <Alert
          message="错误"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Alert
        message="密码安全要求"
        description={
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>密码长度至少 8 位</li>
            <li>密码熵值必须大于 60（基于字符多样性、长度和随机性计算）</li>
            <li>建议包含大小写字母、数字和特殊字符的组合</li>
            <li>避免使用连续字符（如 123456、abcdef）</li>
            <li>避免使用重复字符（如 aaaaa、111111）</li>
          </ul>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Form.Item
          name="oldPassword"
          label="原密码"
          rules={[{ required: true, message: '请输入原密码' }]}
        >
          <Input.Password
            placeholder="请输入原密码"
            autoComplete="current-password"
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="newPassword"
          label="新密码"
          rules={[
            { required: true, message: '请输入新密码' },
            { min: 8, message: '密码长度至少为 8 位' },
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.resolve();
                }

                const validation = validatePasswordStrength(value);
                if (validation.isValid) {
                  return Promise.resolve();
                }

                // 返回第一个反馈作为错误消息
                const firstFeedback = validation.feedback[0];
                return Promise.reject(
                  new Error(firstFeedback || '密码不符合强度要求'),
                );
              },
            },
          ]}
          extra={
            <div style={{ marginTop: 8 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 4,
                }}
              >
                <Typography.Text type="secondary">密码强度</Typography.Text>
                <Typography.Text style={{ color: getEntropyColor() }}>
                  熵值：{passwordEntropy.toFixed(1)} bits - {getEntropyStatus()}
                </Typography.Text>
              </div>
              <Progress
                percent={Math.min(100, (passwordEntropy / 80) * 100)}
                strokeColor={getEntropyColor()}
                showInfo={false}
                size="small"
              />
              {passwordEntropy > 0 && passwordEntropy < 60 && (
                <Typography.Text
                  type="warning"
                  style={{ fontSize: 12, marginTop: 4, display: 'block' }}
                >
                  ⚠️ 熵值需达到 60 以上才符合安全要求
                </Typography.Text>
              )}
              {passwordEntropy >= 60 && (
                <Typography.Text
                  type="success"
                  style={{ fontSize: 12, marginTop: 4, display: 'block' }}
                >
                  ✅ 密码强度符合安全要求
                </Typography.Text>
              )}
            </div>
          }
        >
          <Input.Password
            placeholder="请输入新密码"
            autoComplete="new-password"
            onChange={handlePasswordChange}
            size="large"
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="确认新密码"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: '请再次输入新密码' },
            { validator: validateConfirmPassword },
          ]}
        >
          <Input.Password
            placeholder="请再次输入新密码"
            autoComplete="new-password"
            size="large"
          />
        </Form.Item>
      </Form>

      <Alert
        message="提示"
        description="修改密码后，请使用新密码重新登录。"
        type="warning"
        showIcon
        style={{ marginTop: 16 }}
      />
    </Modal>
  );
};

export default ChangePasswordForm;
